// What the Activity screen asks the kernel, and what it remembers between visits, as plain functions.
//
// Two bugs came from the screen deciding this inline:
//
// - Choosing "One person or app" cleared the person and asked the kernel straight away. The request
//   carried no `subject`, so the kernel answered the Everything question, which is the one thing the
//   reader had just chosen not to ask. When that answer was a refusal the reader was left looking at
//   it, and the box they needed to type into was replaced by a loading screen on every keystroke.
// - The lens lived in component state only, so a reload put it back to Everything.
//
// So a lens that needs a value does not ask until it has one, and the lens and its value are kept in
// the address (`#activity?lens=subject&q=manifest%3Acrowcase`, so a copied link opens the same view)
// and, because the address does not survive a sign-in bounce, in the tab's session storage.
import { parseHash } from "../hash.js";

export const LENS_IDS = ["everything", "record", "subject", "action"];

/** What each lens that needs a value says while it is waiting for one. */
const WAITING = {
  record: "Enter a record as Type/id, for example Organization/01M2…, to read its whole history.",
  subject: "Enter a person or a manifest:app to read the permissions granted or decided for them.",
  action: "Choose an action to read every write of that verb.",
};

const STORAGE_KEY = "kernel.activity.lens";

/**
 * The one question whose answer is nothing: every action, in a window that ended before the log
 * began. A lens that is waiting for a value has no page, and the page is where the kernel's vocabulary
 * arrives, so this is asked once to fill the Action list and the other selects, and its answer is not
 * drawn.
 */
export const VOCABULARY_PROBE = { limit: 1, until: "1970" };

export function needsQualifier(lens) {
  return Object.hasOwn(WAITING, lens);
}

/** The sentence for a lens that has no value yet, or null when it can ask. */
export function waitingFor(lens, qualifier) {
  if (!needsQualifier(lens)) return null;
  return String(qualifier ?? "").trim() ? null : WAITING[lens];
}

/**
 * The query for `GET /audit`, or null when there is nothing to ask yet.
 *
 * Null is not "ask for everything". A lens that needs a value and has none is a question that has not
 * been put, and the only wrong answer to it is the answer to a different question.
 */
export function auditParams({ lens, qualifier, filters, limit, sort, since, until, cursor }) {
  if (waitingFor(lens, qualifier)) return null;
  return {
    limit,
    sort,
    since,
    until,
    cursor,
    ...(needsQualifier(lens) ? { [lens]: String(qualifier).trim() } : {}),
    ...filters,
  };
}

function chosen(lens, qualifier) {
  if (!LENS_IDS.includes(lens)) return null;
  return { lens, qualifier: needsQualifier(lens) ? String(qualifier ?? "") : "" };
}

/**
 * The lens to open with: the address if it names one, else what this tab last had, else Everything.
 * Anything that is not a lens this screen knows falls through to the next, so a hand-edited link or a
 * stale entry cannot open a view that does not exist.
 */
export function readLens({ hash = "", storage = null } = {}) {
  const params = parseHash(hash).params;
  const fromAddress = chosen(params.get("lens"), params.get("q"));
  if (fromAddress) return fromAddress;
  try {
    const saved = JSON.parse(storage?.getItem(STORAGE_KEY) ?? "null");
    const fromTab = chosen(saved?.lens, saved?.qualifier);
    if (fromTab) return fromTab;
  } catch {
    // Storage can be absent, blocked, or hold something else: Everything is a safe place to start.
  }
  return { lens: "everything", qualifier: "" };
}

/** The address for a lens: the section, and after a question mark the lens and its value if it has one. */
export function lensHash(section, lens, qualifier) {
  if (lens === "everything" || !LENS_IDS.includes(lens)) return `#${section}`;
  const params = new URLSearchParams({ lens });
  if (needsQualifier(lens) && qualifier) params.set("q", qualifier);
  return `#${section}?${params}`;
}

/** What to keep in session storage for a lens, as the string `readLens` reads back. */
export function lensRecord(lens, qualifier) {
  return JSON.stringify({ lens, qualifier });
}

export const lensStorageKey = STORAGE_KEY;

/**
 * What to tell the reader when a page lists only part of a timestamp (LO-232), or null.
 *
 * The kernel lists the first part of a timestamp that more rows share than one read will take, and
 * says so in `truncated`. A screen that drew the rows and said nothing would be an audit log answering
 * "nothing else happened", so this is the sentence that goes with the table.
 */
export function crowdedNotice(page) {
  const marks = Array.isArray(page?.truncated) ? page.truncated : [];
  if (!marks.length) return null;
  return {
    title: marks.length === 1
      ? "This page lists only part of one timestamp"
      : `This page lists only part of ${marks.length} timestamps`,
    detail: page.truncated_detail || "",
    marks: marks.map((m) => ({
      key: `${m.action || "record"}@${m.at}`,
      label: `${m.action || "one record"} at ${m.at}`,
      shown: m.shown,
    })),
    // A short page with a cursor, because it ended at the instant, is not the end of the log.
    short: Boolean(page.stopped_early),
  };
}
